import { ChangeDetectorRef, Component, ElementRef, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as moment from 'moment-timezone';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { map, skipWhile, startWith, take } from 'rxjs/operators';
import { SettingsService } from '~billing/services/settings.service';
import { LmoPurchaseOrderAddSupportedWellComponent } from '~lmo/components/lmo-purchase-orders/lmo-purchase-order-detail/lmo-purchase-order-add-supported-well/lmo-purchase-order-add-supported-well.component';
import { LmoPurchaseOrderTimelineComponent } from '~lmo/components/lmo-purchase-orders/lmo-purchase-order-timeline/lmo-purchase-order-timeline.component';
import { LmoFracListSummary } from '~lmo/models/frac.model';
import { PurchaseOrderDetail, PurchaseOrderUpdate } from '~lmo/models/purchaseOrders.model';
import { LmoFracsService } from '~lmo/services';
import { LmoFracFcpoService } from '~lmo/services/lmo-frac-fcpo.service';
import { LmoFracMineService } from '~lmo/services/lmo-frac-mine.service';
import { User } from '~models/user';
import { UserApiService } from '~services/api/user.api.service';
import { AuthService } from '~services/auth.service';
import { UserService } from '~services/user.service';
import { ConfirmActionDialogComponent } from '../../../../ui-components/confirm-action-dialog/confirm-action-dialog.component';

interface PurchaseOrderFormValue {
  costPerTon: number;
  dailyMaximumTonnage: number;
  dailyMinimumTonnage: number;
  shortfallPenaltyCostPerTon: number;
  dailyAllocationReset: Date;
  totalTonnage: number;
  permittedFracIds: number[];
  subscribedSandiUsers: number[];
}

@Component({
  selector: 'sa-lmo-purchase-order-detail',
  styleUrls: ['./lmo-purchase-order-detail.component.scss'],
  templateUrl: './lmo-purchase-order-detail.component.html',
})
export class LmoPurchaseOrderDetailComponent implements OnDestroy {
  @ViewChild('pauseDescription', { static: true }) private pauseModal: TemplateRef<any>;
  @ViewChild('defaultLoadWeightWarning', { static: true }) private defaultLoadWeightWarningModal: TemplateRef<any>;
  @ViewChild('billableMileageWarningModal', { static: true }) private billableMileageWarningModal: TemplateRef<any>;
  @ViewChild('confirmClose', { static: true }) private confirmClose: TemplateRef<any>;
  @ViewChild('fileUpload') private fileUpload: ElementRef;
  public activeNetworkRequest$$ = new BehaviorSubject<boolean>(false);

  public purchaseOrder$: Observable<PurchaseOrderDetail>;
  public uploadingPOAttachment$$ = new BehaviorSubject(false);

  private subscriptions: Subscription[] = [];

  public purchaseOrderForm: FormGroup;
  public purchaseOrderFormDirty$$ = new BehaviorSubject(false);

  public fcpoForms: FormGroup[] = [];
  public fcpoFormsInitialized$$ = new BehaviorSubject<boolean>(false);
  public fcpoFormsDirty$$: BehaviorSubject<boolean>[] = [];
  public fcpoFormsValid$$: BehaviorSubject<boolean>[] = [];

  public timezoneShortName: string;

  public pauseReason = new FormControl(null, [Validators.required]);
  public billableMileage: number;
  public defaultLoadWeight: number;

  public filterUser: FormControl = new FormControl();

  private users$$ = new BehaviorSubject<User[]>([]);
  public filteredUsers$: Observable<User[]>;

  public isGroupedAccountUser$: Observable<boolean>;
  public fracList$: Observable<LmoFracListSummary[]>;

  constructor(
    private purchaseOrderService: LmoFracFcpoService,
    private matDialog: MatDialog,
    private snackBar: MatSnackBar,
    private fb: FormBuilder,
    private userService: UserService,
    private fracMineService: LmoFracMineService,
    private settingService: SettingsService,
    private cd: ChangeDetectorRef,
    private userApiService: UserApiService,
    private authService: AuthService,
    private fracService: LmoFracsService,
  ) {
    this.fracList$ = this.fracService.fracSummaries$;
    this.isGroupedAccountUser$ = this.authService.isGroupedAccountUser$;
    this.purchaseOrder$ = this.purchaseOrderService.currentPurchaseOrder$;

    this.subscriptions.push(
      this.userApiService
        .getWebUsers()
        .pipe(
          map((users) => {
            return users.sort((a, b) => a.name.localeCompare(b.name));
          }),
        )
        .subscribe((users) => {
          this.users$$.next(users);
        }),
    );

    this.filteredUsers$ = combineLatest([this.users$$, this.filterUser.valueChanges.pipe(startWith(''))]).pipe(
      map(([users, filter]) => {
        if (!filter) {
          return users;
        }

        const filterValue = filter.toLowerCase();
        return users.filter((option) => option.name.toLowerCase().indexOf(filterValue) === 0);
      }),
    );

    this.subscriptions.push(
      this.purchaseOrderService.currentPurchaseOrder$
        .pipe(
          skipWhile((po) => !po),
          take(1),
        )
        .subscribe((purchaseOrder) => {
          if (!purchaseOrder) {
            return;
          }

          this.setupForms(purchaseOrder);

          this.timezoneShortName = moment()
            .tz(purchaseOrder.resetAllocationTimezone)
            .format('z');
        }),
    );
  }

  private async setupForms(purchaseOrder: PurchaseOrderDetail) {
    const disableForms = !this.userService.isLMOAccount() || !this.userService.isAdmin() || purchaseOrder.closed;

    const offsetAsTime = moment()
      .startOf('day')
      .add(purchaseOrder.resetAllocationTimeOffset, 'minutes');

    const lmoId = this.userService.accountId();
    const settings = await this.settingService.loadSettings(lmoId).toPromise();

    this.purchaseOrderForm = this.fb.group({
      costPerTon: [{ value: purchaseOrder.costPerTon, disabled: disableForms }],
      dailyAllocationReset: [{ value: offsetAsTime.toDate(), disabled: disableForms }],
      dailyMaximumTonnage: [
        {
          disabled: disableForms,
          value: purchaseOrder.maximumDailyAllocation ? purchaseOrder.maximumDailyAllocation / 2000 : 0,
        },
      ],
      dailyMinimumTonnage: [
        {
          disabled: disableForms,
          value: purchaseOrder.minimumDailyAllocation ? purchaseOrder.minimumDailyAllocation / 2000 : 0,
        },
      ],
      permittedFracIds: [{ value: purchaseOrder.permittedFracIds || [], disabled: disableForms }],
      subscribedSandiUsers: [{ value: purchaseOrder.subscribedUsers.map((user) => user.id), disabled: disableForms }],
      totalTonnage: [{ value: purchaseOrder.allocation / 2000, disabled: disableForms }],
    });

    this.subscriptions.push(
      this.purchaseOrderForm.valueChanges.subscribe(async (value) => {
        const po = await this.purchaseOrder$.pipe(take(1)).toPromise();
        const update: PurchaseOrderUpdate = await this.convertPoEditFormValueToPurchaseOrderUpdate(value);

        const changed =
          update.allocation !== po.allocation ||
          update.costPerTon !== po.costPerTon ||
          update.maximumDailyAllocation !== (po.maximumDailyAllocation || 0) ||
          update.minimumDailyAllocation !== (po.minimumDailyAllocation || 0) ||
          update.resetAllocationTimeOffset !== po.resetAllocationTimeOffset;

        if (changed) {
          this.purchaseOrderFormDirty$$.next(true);
        }
      }),
    );

    for (let i = 0; i < purchaseOrder.currentFracs.length; i++) {
      const currentFrac = purchaseOrder.currentFracs[i];
      const currentFracIndex = i;

      const fcpoForm = this.fb.group({
        billableMileage: [
          { value: currentFrac.billableMileage, disabled: disableForms },
          [Validators.required, Validators.min(0)],
        ],
        defaultLoadWeight: [
          { value: currentFrac.defaultLoadWeightPounds, disabled: disableForms },
          [Validators.min(0)],
        ],
        percentBasedAutoOrderingAllocation: [
          { value: currentFrac.percentBasedAutoOrderingAllocation, disabled: disableForms },
        ],
        poLineItemNumber: [
          { value: currentFrac.poLineItemNumber, disabled: disableForms },
          settings.isPOLineItemNumberMandatory ? [Validators.required] : [],
        ],
      });

      this.fcpoForms.push(fcpoForm);

      const formDirtySubject$ = new BehaviorSubject(false);

      this.fcpoFormsDirty$$.push(formDirtySubject$);

      const formValidSubject$ = new BehaviorSubject(fcpoForm.valid);

      this.fcpoFormsValid$$.push(formValidSubject$);

      this.subscriptions.push(
        fcpoForm.valueChanges.subscribe(async (value) => {
          const po = await this.purchaseOrder$.pipe(take(1)).toPromise();
          const cf = po.currentFracs[currentFracIndex];

          const changed =
            value.billableMileage !== cf.billableMileage ||
            value.defaultLoadWeightPounds !== cf.defaultLoadWeightPounds ||
            value.poLineItemNumber !== cf.poLineItemNumber;

          formDirtySubject$.next(changed);
          formValidSubject$.next(this.fcpoForms[currentFracIndex].valid);
        }),
      );
    }

    this.fcpoFormsInitialized$$.next(true);
    this.cd.markForCheck();
  }

  public getFileExtension(fileName: string): string {
    const lastDot = fileName.lastIndexOf('.');
    return fileName.substring(lastDot + 1);
  }

  public isImage(fileName): boolean {
    const imageExtensions = ['jpeg', 'jpg', 'png'];
    return imageExtensions.includes(this.getFileExtension(fileName));
  }

  public isPDF(fileName): boolean {
    return this.getFileExtension(fileName) === 'pdf';
  }

  public openInWindow(fileUrl) {
    window.open(fileUrl, '_blank');
  }

  public deleteAttachment(fileId, poId) {
    const confirmDialogRef = this.matDialog.open(ConfirmActionDialogComponent, {
      data: {
        cancelButtonText: 'Cancel',
        desc: 'Are you sure you want delete this attachment? This cannot be undone.',
        submitButtonText: 'Delete',
        title: `Delete Attachment`,
      },
    });

    confirmDialogRef.afterClosed().subscribe((keepGoing) => {
      if (!keepGoing) {
        return;
      }

      this.purchaseOrderService.deletePOAttachment$(fileId, poId).subscribe(
        (res) => {
          this.purchaseOrderService.refreshCurrentPurchaseOrder();
          this.snackBar.open('Attachment Deleted Successfully', null, {
            duration: 5000,
          });
        },
        (err) => {
          this.uploadingPOAttachment$$.next(false);
          this.snackBar.open('Deletion Failed!', null, {
            duration: 5000,
          });
          console.error(err, 'Deletion Failed', fileId, poId);
        },
      );
    });
  }

  public onFileChange(event) {
    if (event.target.files && event.target.files.length) {
      const [file] = event.target.files;
      if (this.isImage(file.name) || this.isPDF(file.name)) {
        this.uploadAttachment(file);
      } else {
        this.snackBar.open('Only image and PDF files can be uploaded.', null, {
          duration: 5000,
          panelClass: ['snackbar-error'],
        });
      }
    }
  }

  private async uploadAttachment(file) {
    this.uploadingPOAttachment$$.next(true);

    const po = await this.purchaseOrder$.pipe(take(1)).toPromise();

    if (po.closed) {
      return;
    }

    const fileName = po.mineName + '-' + po.meshType + '-' + po.id;
    this.purchaseOrderService.uploadPOAttachment$(po.id, file, fileName).subscribe(
      (res) => {
        this.uploadingPOAttachment$$.next(false);
        this.purchaseOrderService.refreshCurrentPurchaseOrder();
        this.snackBar.open('File uploaded Successfully', null, {
          duration: 5000,
        });
      },
      (err) => {
        this.uploadingPOAttachment$$.next(false);
        this.snackBar.open('Upload Failed!', null, {
          duration: 5000,
        });
        console.error(err, 'Upload Failed', file);
      },
    );
  }

  private async convertPoEditFormValueToPurchaseOrderUpdate(
    formValue: PurchaseOrderFormValue,
  ): Promise<PurchaseOrderUpdate> {
    const currentPO = await this.purchaseOrderService.currentPurchaseOrder$.pipe(take(1)).toPromise();

    const pickedValue = moment(formValue.dailyAllocationReset);
    const startOfDay = pickedValue.clone().startOf('day');
    const minutesOffSet = pickedValue.diff(startOfDay, 'minutes');
    return {
      allocation: formValue.totalTonnage * 2000,
      costPerTon: formValue.costPerTon,
      id: currentPO.id,
      maximumDailyAllocation: formValue.dailyMaximumTonnage * 2000,
      minimumDailyAllocation: formValue.dailyMinimumTonnage * 2000,
      permittedFracIds: formValue.permittedFracIds,
      resetAllocationTimeOffset: minutesOffSet,
      subscribedUserIds: formValue.subscribedSandiUsers,
    };
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public async updatePO() {
    const update = await this.convertPoEditFormValueToPurchaseOrderUpdate(this.purchaseOrderForm.value);
    const po = await this.purchaseOrder$.pipe(take(1)).toPromise();

    if (po.closed) {
      return;
    }

    this.purchaseOrderFormDirty$$.next(false);

    this.purchaseOrderService.updatePurchaseOrderDetail$(po.id, update).subscribe(
      (res) => {
        if (res) {
          this.snackBar.open('Purchase Order Updated Successfully', null, {
            duration: 5000,
          });
        } else {
          this.snackBar.open('Update Failed!', null, {
            duration: 5000,
            panelClass: ['snackbar-error'],
          });
          this.purchaseOrderFormDirty$$.next(true);
        }
      },
      (err) => {
        this.snackBar.open('Update Failed!', null, {
          duration: 5000,
          panelClass: ['snackbar-error'],
        });
        console.error(err, 'Update Failed');
        this.purchaseOrderFormDirty$$.next(true);
      },
    );
  }

  public promptPause() {
    this.matDialog.open(this.pauseModal, {
      panelClass: ['w-full', 'lg:w-1/2'],
    });
  }

  public async pausePO() {
    if (this.pauseReason.invalid) {
      return;
    }

    const currentPO = await this.purchaseOrder$.pipe(take(1)).toPromise();

    if (currentPO.closed) {
      return;
    }

    try {
      await this.purchaseOrderService.pausePO(currentPO.id, this.pauseReason.value);
      this.purchaseOrderService.refreshCurrentPurchaseOrder();
      this.snackBar.open('PO Paused', null, { duration: 5000 });
    } catch (e) {
      this.snackBar.open('Failed to pause PO', null, { duration: 5000 });
    }
  }

  public async unpausePO() {
    const currentPO = await this.purchaseOrder$.pipe(take(1)).toPromise();

    if (currentPO.closed) {
      return;
    }

    try {
      await this.purchaseOrderService.unpausePO(currentPO.id);
      this.purchaseOrderService.refreshCurrentPurchaseOrder();
      this.snackBar.open('PO Resumed', null, { duration: 5000 });
    } catch (e) {
      this.snackBar.open('Failed to resume PO', null, { duration: 5000 });
    }
  }

  public async updateFcpo(i: number) {
    if (this.fcpoForms[i].invalid) {
      return;
    }

    this.fcpoFormsDirty$$[i].next(false);

    const po = await this.purchaseOrder$.pipe(take(1)).toPromise();
    if (po.closed) {
      return;
    }

    const formValue = this.fcpoForms[i].value;

    const defaultLoadWeightValue = formValue.defaultLoadWeight;
    // Less than half of the default load weight
    if (defaultLoadWeightValue !== null && defaultLoadWeightValue < 21000) {
      this.defaultLoadWeight = defaultLoadWeightValue;
      const confirmed = await this.matDialog
        .open(this.defaultLoadWeightWarningModal, {
          panelClass: ['w-full', 'lg:w-1/2'],
        })
        .afterClosed()
        .toPromise();

      if (!confirmed) {
        this.fcpoFormsDirty$$[i].next(true);
        return;
      }
    }

    const billableMileageValue = formValue.billableMileage;

    if (po.currentFracs[i].billableMileage !== billableMileageValue) {
      this.billableMileage = billableMileageValue;
      const confirmed = await this.matDialog
        .open(this.billableMileageWarningModal, {
          panelClass: ['w-full', 'lg:w-1/2'],
        })
        .afterClosed()
        .toPromise();

      if (!confirmed) {
        this.fcpoFormsDirty$$[i].next(true);
        return;
      }
    }

    try {
      let success = await this.fracMineService
        .setFracMineRouteFromPODetail$(po.mineId, po.currentFracs[i].id, billableMileageValue, defaultLoadWeightValue)
        .toPromise();
      if (!success) {
        this.snackBar.open('Failed to update Purchase Order', null, { duration: 5000, panelClass: ['snackbar-error'] });
        this.fcpoFormsDirty$$[i].next(true);
        return;
      }

      success = await this.purchaseOrderService
        .updateFcpoFromPODetail$(po.currentFracs[i].fcpoId, {
          fracClusterId: po.currentFracs[i].fracClusterId,
          percentBasedAutoOrderingAllocation: formValue.percentBasedAutoOrderingAllocation,
          purchaseOrderId: po.id,
          purchaseOrderLineItemNumber: formValue.poLineItemNumber as string,
        })
        .toPromise();
      if (!success) {
        this.snackBar.open('Failed to update Purchase Order', null, { duration: 5000, panelClass: ['snackbar-error'] });
        this.fcpoFormsDirty$$[i].next(true);
        return;
      }

      this.purchaseOrderService.refreshCurrentPurchaseOrder();
    } catch (err) {
      this.snackBar.open('Failed to update Purchase Order', null, { duration: 5000, panelClass: ['snackbar-error'] });
      this.fcpoFormsDirty$$[i].next(true);
      return;
    }

    this.snackBar.open('Purchase Order Updated Successfully', null, { duration: 5000 });
  }

  public confirmClosePO(id: number) {
    const dialogRef = this.matDialog.open(this.confirmClose, {
      disableClose: true,
      maxWidth: '968px',
      minWidth: '300px',
      width: '30%',
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.closePO(id);
      }
    });
  }

  public closePO(id: number) {
    this.activeNetworkRequest$$.next(true);

    this.purchaseOrderService.closePurchaseOrder$(id).subscribe((success) => {
      if (success) {
        this.purchaseOrderService.refreshCurrentPurchaseOrder();
      }

      this.activeNetworkRequest$$.next(false);
    });
  }

  public onUploadClick(event: MouseEvent) {
    this.fileUpload?.nativeElement?.click();
  }

  public async promptNewSupportedWell() {
    const po = await this.purchaseOrder$.pipe(take(1)).toPromise();
    const lmoId = this.userService.accountId();
    const settings = await this.settingService.loadSettings(lmoId).toPromise();
    const purchaseOrderUpdate = await this.convertPoEditFormValueToPurchaseOrderUpdate(this.purchaseOrderForm.value);

    const dialogRef = this.matDialog.open(LmoPurchaseOrderAddSupportedWellComponent, {
      data: {
        defaultPOLineItemNumber: settings.defaultPOLineItemNumber,
        meshId: po.meshId,
        mineId: po.mineId,
        permittedFracs: po.permittedFracs,
        purchaseOrderUpdate: purchaseOrderUpdate,
      },
      maxWidth: '500px',
      minWidth: '500px',
    });

    const sub = dialogRef.componentInstance.successEvent.subscribe((success: boolean) => {
      if (success) {
        window.location.reload();
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      sub.unsubscribe();
    });
  }

  public async viewTimeline() {
    const po = await this.purchaseOrder$.pipe(take(1)).toPromise();
    this.matDialog.open(LmoPurchaseOrderTimelineComponent, {
      closeOnNavigation: true,
      data: {
        po,
      },
    });
  }
}
